import React, { useCallback, useEffect, useRef } from 'react';
import style from './DividedCodeInput.module.scss';

const inputIdentifierMap = {
  digit1: 'digit-1',
  digit2: 'digit-2',
  digit3: 'digit-3',
  digit4: 'digit-4',
  digit5: 'digit-5',
  digit6: 'digit-6',
};
const inputIdentifierList = Object.values(inputIdentifierMap);

type DividedCodeInputProps = {
  onSuccess?: (...args: any[]) => void;
  error?: string;
  onChange?: (...args: any[]) => void;
};

const DividedCodeInput = ({
  onSuccess,
  error,
  onChange,
}: DividedCodeInputProps) => {
  const className = 'c-DividedCodeInput';
  const classNames = [
    style[className],
    !!error ? style[`${className}--error`] : null,
  ].join(' ');

  const formRef = useRef<HTMLFormElement | null>(null);

  const onInputKeyPress = useCallback(
    e => {
      onChange?.();

      const formEle = formRef.current;
      const currentEle = e.target;

      //check if all of them are filled and then execute the callback
      const isAllInputFilled = inputIdentifierList.filter(
        identifier => !formEle?.[identifier]?.value,
      );

      if (!isAllInputFilled.length) {
        const value = inputIdentifierList
          ?.map(identifier => formEle?.[identifier]?.value)
          ?.join('');
        onSuccess?.({ value, formEle });
      }

      if (formEle) {
        //detect for backspace or arrowLeft
        if (e.keyCode === 8 || e.keyCode === 37) {
          let prevEleIentifier = currentEle.dataset.previous;
          let prev: any = formEle[prevEleIentifier];

          if (prev) prev.focus();
        } else if (
          //detect for different inputs or arrowRight
          (e.keyCode >= 48 && e.keyCode <= 57) ||
          (e.keyCode >= 65 && e.keyCode <= 90) ||
          (e.keyCode >= 96 && e.keyCode <= 105) ||
          e.keyCode === 39
        ) {
          let nextEleIentifier = currentEle.dataset.next;
          const next: any = formEle[nextEleIentifier];

          if (next) next.focus();
        }
      }
    },
    [onChange, onSuccess],
  );

  useEffect(() => {
    const formEle = formRef.current;

    // to focus the first input box
    if (formEle) {
      const firstInputEle = formEle?.[inputIdentifierList[0]];
      firstInputEle.focus();

      inputIdentifierList.forEach(inputName =>
        formEle[inputName].addEventListener('keyup', onInputKeyPress),
      );
    }

    return () => {
      if (formEle) {
        inputIdentifierList.forEach(inputName =>
          formEle[inputName].removeEventListener('keyup', onInputKeyPress),
        );
      }
    };
  }, [onInputKeyPress]);

  return (
    <div className={classNames}>
      <form
        className={style[`${className}__form`]}
        autoComplete="off"
        ref={formRef}
      >
        <div className={style[`${className}__group`]}>
          <input
            type="tel"
            name={inputIdentifierMap.digit1}
            maxLength={1}
            data-next={inputIdentifierMap.digit2}
          />
          <input
            type="tel"
            name={inputIdentifierMap.digit2}
            maxLength={1}
            data-next={inputIdentifierMap.digit3}
            data-previous={inputIdentifierMap.digit1}
          />
          <input
            type="tel"
            name={inputIdentifierMap.digit3}
            maxLength={1}
            data-next={inputIdentifierMap.digit4}
            data-previous={inputIdentifierMap.digit2}
          />
        </div>
        <div className={style[`${className}__divider`]}></div>
        <div className={style[`${className}__group`]}>
          <input
            type="tel"
            name={inputIdentifierMap.digit4}
            maxLength={1}
            data-next={inputIdentifierMap.digit5}
            data-previous={inputIdentifierMap.digit3}
          />
          <input
            type="tel"
            name={inputIdentifierMap.digit5}
            maxLength={1}
            data-next={inputIdentifierMap.digit6}
            data-previous={inputIdentifierMap.digit4}
          />
          <input
            type="tel"
            name={inputIdentifierMap.digit6}
            maxLength={1}
            data-previous={inputIdentifierMap.digit5}
          />
        </div>
      </form>
      {error && <p className={style[`${className}__errorText`]}>{error}</p>}
    </div>
  );
};

DividedCodeInput.defaultProps = {
  onChange: () => {},
  error: '',
};

export default DividedCodeInput;
